import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Modal, Row, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { validaSenha } from "../../common/utils";

function splitName(fullName = "") {
    const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
    return {
        first_name: parts[0] || "Paciente",
        last_name: parts.slice(1).join(" ") || "Portal",
    };
}

function normalizeUsername({ patientName = "", patientCpf = "" }) {
    const cpfDigits = String(patientCpf).replace(/\D/g, "");
    if (cpfDigits) {
        return cpfDigits;
    }

    const normalized = String(patientName)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9_-]/g, "");

    return normalized || "paciente";
}

function mapUsuariosResponse(data) {
    const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];

    return results.map((usuario) => ({
        id: usuario.id,
        email: usuario.email || "",
        username: usuario.username || "",
        is_active: Boolean(usuario.is_active),
        first_name: usuario.first_name || "",
        last_name: usuario.last_name || "",
    }));
}

export default function PacienteAcesso({
    beneficiarioId,
    programaId,
    patientName,
    patientEmail,
    patientCpf,
}) {
    const MySwal = useMemo(() => withReactContent(Swal), []);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [usuarioErrors, setUsuarioErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [usuarioAtual, setUsuarioAtual] = useState({
        id: "",
        email: "",
        username: "",
        is_active: true,
        password: "",
        password_confirm: "",
    });

    const carregarUsuariosBeneficiario = () => {
        if (!beneficiarioId) {
            setUsuarios([]);
            return;
        }

        setLoading(true);
        api
            .get(`/usuarios/?restricao_id=${beneficiarioId}&restricao_tipo=beneficiario`)
            .then((response) => {
                setUsuarios(mapUsuariosResponse(response.data));
            })
            .catch((error) => {
                console.error("Erro ao carregar acessos do paciente:", error);
                MySwal.fire({
                    icon: "error",
                    title: "Erro",
                    text: "Nao foi possivel carregar os acessos do paciente.",
                    showConfirmButton: true,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        carregarUsuariosBeneficiario();
    }, [beneficiarioId]);

    const abrirModal = (usuario = null) => {
        setUsuarioErrors({});

        if (usuario) {
            setUsuarioAtual({
                id: usuario.id,
                email: usuario.email,
                username: usuario.username,
                is_active: usuario.is_active,
                password: "",
                password_confirm: "",
            });
            setShowModal(true);
            return;
        }

        setUsuarioAtual({
            id: "",
            email: patientEmail || "",
            username: normalizeUsername({ patientName, patientCpf }),
            is_active: true,
            password: "",
            password_confirm: "",
        });
        setShowModal(true);
    };

    const fecharModal = () => {
        setShowModal(false);
        setUsuarioErrors({});
        setShowPassword(false);
        setShowPasswordConfirm(false);
    };

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setUsuarioAtual((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const nextErrors = {};
        if (!usuarioAtual.username) {
            nextErrors.username = "Usuario obrigatorio";
        }
        if (!usuarioAtual.email) {
            nextErrors.email = "E-mail obrigatorio";
        }

        const isCreate = !usuarioAtual.id;
        if (isCreate && !usuarioAtual.password) {
            nextErrors.password = "Senha obrigatoria";
        }

        if (usuarioAtual.password) {
            if (!validaSenha(usuarioAtual.password)) {
                nextErrors.password = "A senha nao atende aos requisitos minimos.";
            }
            if (usuarioAtual.password !== usuarioAtual.password_confirm) {
                nextErrors.password_confirm = "As senhas nao coincidem.";
            }
        }

        if (Object.keys(nextErrors).length > 0) {
            setUsuarioErrors(nextErrors);
            return;
        }

        const { first_name, last_name } = splitName(patientName);
        const payload = {
            first_name,
            last_name,
            email: usuarioAtual.email,
            username: usuarioAtual.username,
            is_active: usuarioAtual.is_active,
            grupos: [],
            programa_id: programaId || 0,
        };

        if (usuarioAtual.password) {
            payload.password = usuarioAtual.password;
        }

        if (!usuarioAtual.id) {
            payload.restricoes = {
                id: beneficiarioId,
                tipo: "beneficiario",
            };
        }

        setLoading(true);
        api({
            method: usuarioAtual.id ? "put" : "post",
            url: usuarioAtual.id ? `/usuarios/${usuarioAtual.id}/` : "/usuarios/",
            data: payload,
        })
            .then(() => {
                MySwal.fire({
                    icon: "success",
                    title: "Sucesso",
                    text: usuarioAtual.id
                        ? "Acesso do paciente atualizado com sucesso."
                        : "Acesso do paciente criado com sucesso.",
                    showConfirmButton: true,
                });
                fecharModal();
                carregarUsuariosBeneficiario();
            })
            .catch((error) => {
                console.error("Erro ao salvar acesso do paciente:", error);

                const errorsFromApi = {};
                let mensagem = "Erro ao salvar o acesso do paciente.";
                const data = error.response?.data;

                if (data && typeof data === "object") {
                    Object.entries(data).forEach(([field, msg]) => {
                        const text = Array.isArray(msg) ? msg.join(" ") : String(msg);
                        if (field === "detail" || field === "non_field_errors") {
                            mensagem = text;
                        } else {
                            errorsFromApi[field] = text;
                        }
                    });
                }

                setUsuarioErrors(errorsFromApi);
                MySwal.fire({
                    icon: "error",
                    title: "Erro",
                    text: mensagem,
                    showConfirmButton: true,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleInativar = (usuarioId) => {
        MySwal.fire({
            icon: "warning",
            title: "Atencao",
            text: "Deseja inativar este acesso do paciente?",
            showCancelButton: true,
            confirmButtonText: "Sim",
            cancelButtonText: "Nao",
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }

            setLoading(true);
            api.delete(`/usuarios/${usuarioId}/`)
                .then(() => {
                    MySwal.fire({
                        icon: "success",
                        title: "Sucesso",
                        text: "Acesso inativado com sucesso.",
                        showConfirmButton: true,
                    });
                    carregarUsuariosBeneficiario();
                })
                .catch((error) => {
                    console.error("Erro ao inativar acesso do paciente:", error);
                    MySwal.fire({
                        icon: "error",
                        title: "Erro",
                        text: "Nao foi possivel inativar o acesso.",
                        showConfirmButton: true,
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    return (
        <Card className="cardDestaque">
            <Card.Body>
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h4>Dados de Acesso</h4>
                        <p className="text-muted mb-0">
                            Crie ou altere o usuario e a senha que o paciente usara para acessar o portal.
                        </p>
                    </Col>
                    <Col className="text-end">
                        {usuarios.length === 0 && (
                            <Button variant="success" size="sm" onClick={() => abrirModal()} disabled={loading}>
                                Criar acesso
                            </Button>
                        )}
                    </Col>
                </Row>

                <Table responsive className="custom-table mt-3">
                    <thead>
                        <tr>
                            <th>Ativo</th>
                            <th>Nome</th>
                            <th>E-mail</th>
                            <th>Usuario</th>
                            <th>Acoes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="5" className="text-center">Carregando...</td>
                            </tr>
                        ) : usuarios.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="text-center">Nenhum acesso cadastrado.</td>
                            </tr>
                        ) : (
                            usuarios.map((usuario) => (
                                <tr key={usuario.id}>
                                    <td>
                                        {usuario.is_active ? (
                                            <span className="badge bg-success">Ativo</span>
                                        ) : (
                                            <span className="badge bg-danger">Inativo</span>
                                        )}
                                    </td>
                                    <td>{`${usuario.first_name} ${usuario.last_name}`.trim()}</td>
                                    <td>{usuario.email}</td>
                                    <td>{usuario.username}</td>
                                    <td>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="me-2"
                                            onClick={() => abrirModal(usuario)}
                                        >
                                            Editar
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleInativar(usuario.id)}
                                        >
                                            Inativar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <Modal show={showModal} onHide={fecharModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{usuarioAtual.id ? "Editar acesso" : "Criar acesso"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Row>
                                <Col sm={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Usuario</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="username"
                                            value={usuarioAtual.username}
                                            onChange={handleChange}
                                        />
                                        <Form.Text className="text-danger">{usuarioErrors.username}</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col sm={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>E-mail</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={usuarioAtual.email}
                                            onChange={handleChange}
                                        />
                                        <Form.Text className="text-danger">{usuarioErrors.email}</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{usuarioAtual.id ? "Nova senha" : "Senha"}</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                value={usuarioAtual.password}
                                                onChange={handleChange}
                                                placeholder={usuarioAtual.id ? "Preencha para trocar" : "Senha"}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                            >
                                                {showPassword ? "Ocultar" : "Ver"}
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-danger">{usuarioErrors.password}</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Confirmar senha</Form.Label>
                                        <InputGroup>
                                            <Form.Control
                                                type={showPasswordConfirm ? "text" : "password"}
                                                name="password_confirm"
                                                value={usuarioAtual.password_confirm}
                                                onChange={handleChange}
                                            />
                                            <Button
                                                variant="outline-secondary"
                                                type="button"
                                                onClick={() => setShowPasswordConfirm((prev) => !prev)}
                                            >
                                                {showPasswordConfirm ? "Ocultar" : "Ver"}
                                            </Button>
                                        </InputGroup>
                                        <Form.Text className="text-danger">{usuarioErrors.password_confirm}</Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col sm={12}>
                                    <Form.Check
                                        type="checkbox"
                                        name="is_active"
                                        label="Acesso ativo"
                                        checked={usuarioAtual.is_active}
                                        onChange={handleChange}
                                    />
                                </Col>
                            </Row>
                            <div className="small text-muted mt-3">
                                A troca de senha feita por operadores nesta tela não exige verificação adicional.
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={fecharModal}>
                                Cancelar
                            </Button>
                            <Button variant="success" type="submit" disabled={loading}>
                                {loading ? "Salvando..." : "Salvar"}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Card.Body>
        </Card>
    );
}